import { useState, useCallback } from 'react'
import { ErpFrame } from '@ca/common'
import EmptyResult from '@ui/components/EmptyResult'
import Input from '@ui/forms/Input'
import useSaveFrame from '@screens/frame/settings/useSaveFrame'
import { trpc } from 'api'
import { router } from 'expo-router'
import { useTranslation } from 'react-i18next'

export default function FrameSelectSearch() {
  const { t } = useTranslation(['ui', 'frame'])
  const text = 'frame:select'
  const inputText = 'frame:search.input'

  const [localQuery, setLocalQuery] = useState('')
  const [foundFrame, setFoundFrame] = useState<ErpFrame | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  const [query] = trpc.useQueries((t) => [
    t.frames.list(
      {
        limit: 2,
        query: localQuery,
      },
      {
        placeholderData: (v) => v,
        refetchOnWindowFocus: false,
        enabled: false,
        refetchOnMount: false,
        retryOnMount: false,
        retry: false,
        refetchOnReconnect: false,
        networkMode: 'online',
      }
    ),
  ])

  const showLoading = query.isFetching || query.isRefetching

  const { save: saveFrame } = useSaveFrame({})

  const handleResult = useCallback(
    (data: ErpFrame[]) => {
      if (data.length === 1) {
        setFoundFrame(data[0])
        return
      }
      setFoundFrame(null)
      router.navigate(`/frame/search?q=${localQuery}`)
    },
    [localQuery]
  )

  const onSearch = useCallback(async () => {
    if (!localQuery.trim()) {
      setLocalError('frame:search.input.error')
      return
    }
    setFoundFrame(null)
    const res = await query.refetch()
    handleResult(res.data?.data || [])
  }, [handleResult, query, localQuery])

  const onSave = useCallback(() => {
    if (!foundFrame) return
    saveFrame({ frameId: foundFrame.id })
  }, [foundFrame, saveFrame])

  const onChangeText = useCallback((text: string) => {
    setLocalError(null)
    setLocalQuery(text)
    setFoundFrame(null)
  }, [])

  return (
    <EmptyResult
      disableAnimation
      title={t(`${text}.empty.saved.title`)}
      desc={t(`${text}.empty.saved.desc`)}
      button={
        foundFrame
          ? {
              loading: showLoading,
              label: t('ui:saveFrame'),
              iconEnd: 'arrow-narrow-right',
              onPress: onSave,
            }
          : {
              loading: showLoading,
              label: t('ui:search'),
              iconEnd: 'search',
              variant: 'outlined',
              onPress: onSearch,
            }
      }
    >
      <Input
        label={t(`${inputText}.label`)}
        placeholder={t(`${inputText}.placeholder`)}
        value={foundFrame ? foundFrame.default_code : localQuery}
        onChangeText={foundFrame ? undefined : onChangeText}
        readonly={!!foundFrame}
        isSuccess={!!foundFrame}
        autoCorrect={false}
        errorMessage={localError ?? undefined}
      />
    </EmptyResult>
  )
}
